export const InfoDialogWidth = 450;
export const settingsDialogWidth = 280;
export const tooltipDelay = 1000;
export const defaultRecordsLimit = 6;
export const numOfDefInitialCols = 5;

export enum Templates {
    list = 'list',
    column = 'column'
}

export interface BasicFormData {
    entityId: string;
    formName: string;
    subFormName: string;
    selectedFields: string[];
    template: Templates;
    hasAttachment?: boolean;
    formTitle?: string;
    targetColName?: string;
}

export type MapKeys = 'CUSTOMERS' | 'LOGPART' | 'SUPPLIERS' | 'DOCUMENTS_p';

export type InfoMap = {
    [key in MapKeys]: BasicFormData[];
};

export const FORM_TITLE_MAP = {
    CUSTOMERS: {en: 'Customers', he: 'לקוחות'},
    CUSTPERSONNEL: {en: 'Customer Contacts', he: 'אנשי קשר ללקוח'},
    FNCCUST: {en: 'Financ. Parameters for Customers', he: 'הגדרות כספים ללקוחות'},
    CUSTOBLIGO: {en: 'Customer Credit', he: 'אשראי ואובליגו של הלקוח'},
    LOGPART: {en: 'Part Catalogue', he: 'כרטיס פריט'},
    LOGCOUNTERS: {en: 'Part Availability', he: 'זמינות מוצר'},
    PARTBALANCE: {en: 'Inventory for Part', he: 'מלאי למוצר'},
    LOGPARTCUST: {en: 'Discounts & Spec. Prices by Cust', he: 'הנחות ומחירים מיוחדים ללקוחות'},
    SUPPLIERS: {en: 'Vendors', he: 'ספקים'},
    FNCSUP: {en: 'Financial Parameters for Vendors', he: 'הגדרות כספים לספקים'},
    SUPPERSONNEL: {en: 'Vendor Contacts', he: 'אנשי קשר לספק'},
    SUPPORDERS: {en: 'Open Purchase Orders', he: 'הזמנות רכש פתוחות'},
    DOCUMENTS_p: {en: 'Projects', he: 'פרויקטים'},
    PROJPROFIT: {en: 'Revenue and Profit', he: 'הכנסות ורווחיות'},
    TRANSORDER_qp: {en: 'Reports of Project Hrs/Expenses', he: 'דווח שעות והוצאות לפרויקט'},
    PROJITEMS: {en: 'Finished Products', he: 'תוצרים סופיים'}
};

const CUSTOMER_NAME_MAP = [
    {
        entityId: 'CUSTOMERS',
        formName: 'CUSTOMERS',
        selectedFields: ['ADDRESS', 'STATE', 'STATDES'],
        subFormName: '',
        template: Templates.column
    },
    {
        entityId: 'CUSTPERSONNEL',
        formName: 'CUSTOMERS',
        subFormName: 'CUSTPERSONNEL',
        selectedFields: ['NAME', 'POSITIONDES', 'CELLPHONE', 'EMAIL'],
        template: Templates.list,
        hasAttachment: true
    },
    {
        entityId: 'FNCCUST',
        formName: 'FNCCUST',
        subFormName: '',
        selectedFields: ['PAYCODE', 'PAYDES', 'TAXCODE', 'TAXDES', 'MAX_CREDIT', 'OBCODE'],
        template: Templates.column
    },
    {
        entityId: 'CUSTOBLIGO',
        formName: 'FNCCUST',
        subFormName: 'CUSTOBLIGO',
        selectedFields: ['ORD_DEBIT', 'DOC_DEBIT', 'ACC_DEBIT', 'OBLIGO', 'OB_CODE_ENV'],
        template: Templates.column
    }
];

const PARTS_MAP = [
    {
        entityId: 'LOGPART',
        formName: 'LOGPART',
        subFormName: '',
        selectedFields: [
            'EXTFILENAME',
            'FAMILYNAME',
            'FAMILYDES',
            'TYPE',
            'PARTTYPEDES',
            'BASEPLCODE',
            'BASEPLPRICE',
            'SUPNAME',
            'SUPDES'
        ],
        template: Templates.column,
        hasAttachment: true
    },
    {
        entityId: 'LOGCOUNTERS',
        formName: 'LOGPART',
        subFormName: 'LOGCOUNTERS',
        selectedFields: ['BALANCE', 'PORDERS', 'ORDERS', 'DIFF', 'SELLBALANCE', 'UNITNAME4'],
        template: Templates.column
    },
    {
        entityId: 'PARTBALANCE',
        formName: 'LOGPART',
        subFormName: 'PARTBALANCE',
        selectedFields: ['WARHSNAME', 'LOCNAME', 'TBALANCE', 'UNITNAME'],
        template: Templates.list
    },
    {
        entityId: 'LOGPARTCUST',
        formName: 'LOGPART',
        subFormName: 'LOGPARTCUST',
        selectedFields: ['CUSTNAME', 'CUSTDES', 'PARTPRICE'],
        template: Templates.list
    }
];

const VENDOR_MAP = [
    {
        entityId: 'SUPPLIERS',
        formName: 'SUPPLIERS',
        subFormName: '',
        selectedFields: ['ADDRESS', 'STATE', 'COUNTRYNAME', 'PAYDES', 'COMPNUM', 'STATDES'],
        template: Templates.column
    },
    {
        entityId: 'FNCSUP',
        formName: 'FNCSUP',
        subFormName: '',
        selectedFields: ['ACCNAME', 'WTAXPERCENT', 'WTAXDATE', 'CONFIRMEDDATE', 'VATNUM'],
        template: Templates.column
    },
    {
        entityId: 'SUPPERSONNEL',
        formName: 'SUPPLIERS',
        subFormName: 'SUPPERSONNEL',
        selectedFields: ['NAME', 'PHONENUM', 'CELLPHONE', 'EMAIL'],
        template: Templates.list
    },
    {
        entityId: 'SUPPORDERS',
        formName: 'SUPPLIERS',
        subFormName: 'SUPPORDERS',
        selectedFields: ['ORDNAME', 'CURDATE', 'DETAILS', 'TOTPRICE', 'CODE'],
        template: Templates.list
    }
];

const PROJECTS_MAP = [
    {
        entityId: 'DOCUMENTS_p',
        formName: 'DOCUMENTS_p',
        subFormName: '',
        selectedFields: [
            'DOCNO',
            'PROJDES',
            'CUSTNAME',
            'CUSTDES',
            'MUSERLOGIN',
            'PROJTYPECODE',
            'PROJTYPEDES',
            'STATDES'
        ],
        template: Templates.column
    },
    {
        entityId: 'PROJPROFIT',
        formName: 'DOCUMENTS_p',
        subFormName: 'PROJPROFIT',
        selectedFields: ['TOTINCOME', 'TOTCOST', 'TOTPROFIT', 'CODE'],
        template: Templates.column
    },
    {
        entityId: 'TRANSORDER_qp',
        formName: 'DOCUMENTS_p',
        subFormName: 'TRANSORDER_qp',
        selectedFields: [
            'CURDATE',
            'USERLOGIN',
            'PARTNAME',
            'PDES',
            'ORDNAME',
            'STIME',
            'ETIME',
            'TQUANT'
        ],
        template: Templates.list
    },
    {
        entityId: 'PROJITEMS',
        formName: 'DOCUMENTS_p',
        subFormName: 'PROJITEMS',
        selectedFields: [
            'PARTNAME',
            'PDES',
            'TQUANT',
            'TUNITNAME',
            'PRICE',
            'CODE',
            'QPRICE',
            'INSTDATE',
            'WARDATEFINAL'
        ],
        template: Templates.list
    }
];

export const INFO_MAP = {
    CUSTOMERS: CUSTOMER_NAME_MAP,
    LOGPART: PARTS_MAP,
    SUPPLIERS: VENDOR_MAP,
    DOCUMENTS_p: PROJECTS_MAP
};

export const PROJECTS_TARGET_COL_NAME = {PROJDOCNO: 'DOCNO', PROJNO: 'DOCNO'};
